/**
 * Focus jamais masqué par le dock ni par l'en-tête (WCAG 2.4.11, AGENTS.md › Accessibilité du socle) : complément, dans
 * la coquille, du `scroll-padding` des volets (core/accessibilite.css). Décision du DSI sur l'Issue #295 (02/11/2026) :
 * le correctif est dans la coquille, pas dans `TextArea`.
 *
 * Le navigateur fait défiler le volet pour montrer l'élément qui reçoit le focus, mais pas toujours l'élément entier :
 * au focus d'une zone de texte multiligne, Chrome amène seulement le curseur à l'écran et le bas du champ peut rester
 * sous le dock. Au focus d'un élément du contenu d'une page (descendant d'un volet `.agx-volet`) :
 *  - s'il tient dans la zone libre du volet (sa hauteur moins les réserves de l'en-tête et du dock, `scroll-padding`),
 *    il est ramené entier : `scrollIntoView({ block: 'nearest' })`, qui respecte le `scroll-padding` et ne bouge rien
 *    si l'élément est déjà entier ;
 *  - s'il est plus haut que cette zone, son haut est montré sous l'en-tête (`block: 'start'`).
 * Focus donné au pointeur (clic ou toucher sur l'élément, ou dans un conteneur focalisable) : rien ne bouge. 2.4.11 vise
 * le focus au clavier, et le contenu ne doit pas glisser sous le pointeur pendant un clic (le bouton enfoncé changerait
 * de place avant le relâchement). Le focus donné par un script (premier champ en erreur, retour d'une fenêtre) est
 * traité comme au clavier.
 *
 * Défilement – 5 informations : ce qui bouge = le volet de la page ; déclencheur = focus au clavier ou par un script
 * d'un élément en partie sous le dock ou sous l'en-tête ; mouvement = défilement vertical du volet, juste ce qu'il faut ;
 * durée = aucune, défilement immédiat comme celui du navigateur au focus (l'élément est visible dès qu'il a le focus) ;
 * mouvement réduit = identique, aucun défilement animé.
 */
import React from 'react';
import { zoomRacine } from '../../design';

/** Conteneur défilant d'une page ouverte (pages/Volets.tsx), qui porte le `scroll-padding` du dock et de l'en-tête. */
const VOLET = '.agx-volet';

/** Réserve (px, échelle de la page) d'un côté du volet : `scroll-padding` calculé ; `auto` ou absent → 0. */
function reserve(style: CSSStyleDeclaration, cote: 'top' | 'bottom'): number {
  const px = parseFloat(style.getPropertyValue(`scroll-padding-${cote}`));
  return Number.isFinite(px) ? px : 0;
}

/**
 * Ramène l'élément entier dans la zone libre de son volet, ou son haut s'il est plus grand qu'elle. Sans effet hors
 * d'un volet (dock, en-tête, fenêtres et menus en portail) et sur le volet lui-même (cible du lien d'évitement).
 */
export function garderFocusVisible(el: Element): void {
  const volet = el.parentElement?.closest<HTMLElement>(VOLET);
  if (!volet || typeof el.scrollIntoView !== 'function') return;
  const style = getComputedStyle(volet);
  // Mesures d'écran (getBoundingClientRect) et longueurs CSS (scroll-padding) : le zoom de la racine les sépare.
  const libre = volet.getBoundingClientRect().height - (reserve(style, 'top') + reserve(style, 'bottom')) * zoomRacine();
  const entier = el.getBoundingClientRect().height <= libre;
  el.scrollIntoView({ block: entier ? 'nearest' : 'start', inline: 'nearest', behavior: 'instant' });
}

type Gestionnaires = Required<Pick<React.DOMAttributes<HTMLElement>, 'onPointerDownCapture' | 'onKeyDownCapture' | 'onFocusCapture'>>;

/**
 * Gestionnaires à poser sur la racine de la coquille : chaque focus pris dans le contenu d'une page passe par
 * `garderFocusVisible`, sauf celui que donne le pointeur. Un appui du pointeur est noté jusqu'au focus suivant (ou
 * jusqu'à une touche du clavier) : ce focus-là est ignoré s'il contient l'endroit touché.
 */
export function useFocusAuDessusDuDock(): Gestionnaires {
  const pointe = React.useRef<Node | null>(null);
  return React.useMemo<Gestionnaires>(() => ({
    onPointerDownCapture: (e) => { pointe.current = e.target instanceof Node ? e.target : null; },
    onKeyDownCapture: () => { pointe.current = null; },
    onFocusCapture: (e) => {
      const touche = pointe.current;
      pointe.current = null;
      const el = e.target;
      if (!(el instanceof Element) || (touche && el.contains(touche))) return;
      garderFocusVisible(el);
    },
  }), []);
}
